import React, { useState } from 'react';
import { apiClient } from '../../../api/client';
import { InventorySummary } from './InventorySummary';
import { TableMap } from './TableMap';
import { FloorplanView } from './FloorplanView';
import { VisualTableLayout } from './VisualTableLayout';
import { TableNamesView } from './TableNamesView';
import { ViewToggle } from './ViewToggle';
import { TableDetail } from './TableDetail';
import { GuestSearch, GuestResult } from './GuestSearch';
import { BlockDialog } from './BlockDialog';
import { CheckInPanel } from './CheckInPanel';
import { SeatingEnginePanel } from './SeatingEnginePanel';
import { TableTransferPanel } from './TableTransferPanel';
import { AuditLogPanel } from './AuditLogPanel';
import { ToastProvider, useToast } from './Toast';
import { SetupView } from './SetupView';
import { BatchAssignmentPanel } from './batch-assignment/BatchAssignmentPanel';
import { SeatingIntelligencePanel } from './SeatingIntelligencePanel';
import { AISeatingPanel } from './AISeatingPanel';
import { AvailableSeatsView } from './AvailableSeatsView';
import { PRIMARY, TEXT_MUTED, BORDER_LIGHT, BG_PAGE } from './designTokens';

type SubView = 'table-map' | 'check-in' | 'seating-engine' | 'reports' | 'setup' | 'batch-assignment' | 'intelligence' | 'ai-seating' | 'transfer' | 'available-seats';

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '0.5rem',
    backgroundColor: BG_PAGE,
    padding: '0.5rem 0',
  },
  subNav: {
    display: 'flex',
    gap: '0',
    marginBottom: '1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  subTab: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: TEXT_MUTED,
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  subTabActive: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: PRIMARY,
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-1px',
    transition: 'color 0.2s, border-color 0.2s',
  },

};

const SUB_VIEWS: { key: SubView; label: string }[] = [
  { key: 'table-map', label: 'Table Map' },
  { key: 'check-in', label: 'Check-In' },
  { key: 'seating-engine', label: 'Seating Engine' },
  { key: 'batch-assignment', label: 'Batch Assignment' },
  { key: 'intelligence', label: '🧠 Intelligence' },
  { key: 'ai-seating', label: '🤖 AI Seating' },
  { key: 'available-seats', label: '🪑 Available' },
  { key: 'reports', label: 'Reports' },
  { key: 'transfer', label: '🔄 Transfer' },
  { key: 'setup', label: 'Setup' },
];

export function TableManagementTab() {
  return (
    <ToastProvider>
      <TableManagementTabInner />
    </ToastProvider>
  );
}

function TableManagementTabInner() {
  const [activeView, setActiveView] = useState<SubView>('table-map');
  const [viewMode, setViewMode] = useState<'grid' | 'floorplan' | 'visual' | 'table-names'>('grid');
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [blockDialogTable, setBlockDialogTable] = useState<{
    id: string;
    tableNumber: number;
    capacity: number;
  } | null>(null);
  const [assignSeatId, setAssignSeatId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { showToast } = useToast();

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const handleSelectTable = (tableId: string) => {
    setSelectedTableId(tableId);
  };

  const handleCloseDetail = () => {
    setSelectedTableId(null);
  };

  const handleAssignSeat = (seatId: string) => {
    setAssignSeatId(seatId);
  };

  const handleGuestSelected = async (guest: GuestResult) => {
    if (!assignSeatId) return;
    try {
      await apiClient(`/table-management/seats/${assignSeatId}/assign`, {
        method: 'POST',
        body: { guestId: guest.id },
      });
      setAssignSeatId(null);
      showToast('success', `${guest.fullName} assigned successfully.`);
      // Refresh table detail and map
      triggerRefresh();
      setSelectedTableId((prev) => {
        const id = prev;
        setSelectedTableId(null);
        setTimeout(() => setSelectedTableId(id), 50);
        return prev;
      });
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string; error?: string };
      if (apiErr?.statusCode === 409) {
        showToast('error', apiErr?.message || 'Conflict: seat was already assigned. Refreshing...');
        triggerRefresh();
        setSelectedTableId((prev) => {
          const id = prev;
          setSelectedTableId(null);
          setTimeout(() => setSelectedTableId(id), 50);
          return prev;
        });
      } else {
        showToast('error', apiErr?.message || 'Failed to assign guest.');
      }
    }
  };

  const handleBlockSeat = async (seatId: string) => {
    const reason = window.prompt('Block reason (optional):');
    try {
      await apiClient('/table-management/seats/hard-block', {
        method: 'POST',
        body: { seatIds: [seatId], reason: reason || undefined },
      });
      showToast('success', 'Seat blocked.');
      triggerRefresh();
      // Refresh the table detail panel
      setSelectedTableId((prev) => {
        const id = prev;
        setSelectedTableId(null);
        setTimeout(() => setSelectedTableId(id), 50);
        return prev;
      });
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to block seat.');
    }
  };

  const handleHoldSeat = async (seatId: string) => {
    const reason = window.prompt('Hold reason (optional):');
    // Hold indefinitely until manually released (far-future expiration)
    const expiration = new Date('2099-12-31T23:59:59Z').toISOString();
    try {
      await apiClient('/table-management/seats/soft-block', {
        method: 'POST',
        body: { seatIds: [seatId], expiration, holdReason: reason || 'Manual hold' },
      });
      showToast('success', 'Seat placed on hold (until manually released).');
      triggerRefresh();
      // Refresh the table detail panel
      setSelectedTableId((prev) => {
        const id = prev;
        setSelectedTableId(null);
        setTimeout(() => setSelectedTableId(id), 50);
        return prev;
      });
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to hold seat.');
    }
  };

  return (
    <div style={styles.container}>
      {/* Persistent Inventory Summary */}
      <InventorySummary refreshKey={refreshKey} />

      {/* Sub-navigation */}
      <nav style={styles.subNav} aria-label="Table management views">
        {SUB_VIEWS.map((view) => (
          <button
            key={view.key}
            style={activeView === view.key ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveView(view.key)}
            aria-current={activeView === view.key ? 'page' : undefined}
          >
            {view.label}
          </button>
        ))}
      </nav>

      {/* Sub-view content */}
      {activeView === 'table-map' && (
        <>
          <ViewToggle activeView={viewMode} onChange={setViewMode} />
          {viewMode === 'grid' ? (
            <TableMap onSelectTable={handleSelectTable} refreshKey={refreshKey} />
          ) : viewMode === 'floorplan' ? (
            <FloorplanView onSelectTable={handleSelectTable} refreshKey={refreshKey} />
          ) : viewMode === 'visual' ? (
            <VisualTableLayout onSelectTable={handleSelectTable} refreshKey={refreshKey} />
          ) : (
            <TableNamesView refreshKey={refreshKey} />
          )}
        </>
      )}

      {activeView === 'check-in' && <CheckInPanel />}

      {activeView === 'seating-engine' && <SeatingEnginePanel />}

      {activeView === 'batch-assignment' && <BatchAssignmentPanel />}

      {activeView === 'intelligence' && <SeatingIntelligencePanel />}

      {activeView === 'ai-seating' && <AISeatingPanel />}

      {activeView === 'available-seats' && <AvailableSeatsView />}

      {activeView === 'transfer' && <TableTransferPanel />}

      {activeView === 'reports' && (
        <React.Suspense fallback={<div style={{ padding: '2rem', color: '#666' }}>Loading...</div>}>
          <ErrorBoundaryWrapper>
            <AuditLogPanel />
          </ErrorBoundaryWrapper>
        </React.Suspense>
      )}

      {activeView === 'setup' && <SetupView />}

      {/* Table Detail slide-out panel */}
      {selectedTableId && (
        <TableDetail
          tableId={selectedTableId}
          onClose={handleCloseDetail}
          onAssignSeat={handleAssignSeat}
          onBlockSeat={handleBlockSeat}
          onHoldSeat={handleHoldSeat}
          onMutationSuccess={triggerRefresh}
        />
      )}

      {/* Guest Search for seat assignment */}
      {assignSeatId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setAssignSeatId(null);
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Assign guest to seat"
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '1.5rem',
              width: '500px',
              maxWidth: '90vw',
              maxHeight: '70vh',
              overflowY: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f2d1f' }}>
                Assign Guest to Seat
              </h3>
              <button
                onClick={() => setAssignSeatId(null)}
                style={{ border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }}
                aria-label="Close guest search"
              >
                ✕
              </button>
            </div>
            <GuestSearch onSelect={handleGuestSelected} placeholder="Search for a guest to assign..." />
          </div>
        </div>
      )}

      {/* Block Dialog */}
      {blockDialogTable && (
        <BlockDialog
          tableId={blockDialogTable.id}
          tableNumber={blockDialogTable.tableNumber}
          capacity={blockDialogTable.capacity}
          onClose={() => setBlockDialogTable(null)}
          onSuccess={() => {
            setBlockDialogTable(null);
            showToast('success', 'Block applied successfully.');
            triggerRefresh();
            // Refresh table detail
            if (selectedTableId) {
              const id = selectedTableId;
              setSelectedTableId(null);
              setTimeout(() => setSelectedTableId(id), 50);
            }
          }}
        />
      )}
    </div>
  );
}


class ErrorBoundaryWrapper extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: string }> {
  state = { hasError: false, error: '' };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#d32f2f' }}>
          <p style={{ fontWeight: 600 }}>Reports panel encountered an error</p>
          <p style={{ fontSize: '0.85rem', color: '#666' }}>{this.state.error}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: '' })}
            style={{ marginTop: '1rem', padding: '8px 16px', borderRadius: '6px', border: '1px solid #ccc', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
